import React from 'react';
import { CheckCircle2, Circle, Truck, Package, MapPin, Calendar, CheckSquare, DollarSign, FileText, Clock, CreditCard, BadgeCheck, ChevronRight, MessageSquare, Send, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * CompactDualWorkflow - Version compacte des workflows côte à côte
 * Design optimisé pour moins d'espace vertical
 * Inclut maintenant: Commercial (admin only), Opération et Finance
 */

// Statuts commerciaux (admin only)
const COMMERCIAL_STAGES = [
  { id: 'demande_enregistree', label: 'Demande', icon: FileText },
  { id: 'en_attente_de_devis', label: 'Création', icon: FileText },
  { id: 'en_attente_validation_client', label: 'Envoyé', icon: Send },
  { id: 'en_negociation', label: 'Négo', icon: MessageSquare },
  { id: 'devis_accepte', label: 'Accepté', icon: ThumbsUp }
];

// Statuts opération
const OPERATION_STAGES = [
  { id: 'planifiee', label: 'Planifiée', icon: Calendar },
  { id: 'prete_au_chargement', label: 'Prête', icon: CheckSquare },
  { id: 'en_cours_de_chargement', label: 'Chargement', icon: Package },
  { id: 'charge_en_route', label: 'En route', icon: Truck },
  { id: 'livree', label: 'Livrée', icon: MapPin },
  { id: 'terminee', label: 'Terminée', icon: CheckCircle2 }
];

// Statuts finance
const FINANCE_STAGES = [
  { id: 'quote_accepted', label: 'Devis OK', icon: FileText },
  { id: 'invoiced', label: 'Facturée', icon: FileText },
  { id: 'pending_payment', label: 'Attente', icon: Clock },
  { id: 'partially_paid', label: 'Partiel', icon: CreditCard },
  { id: 'paid', label: 'Payée', icon: BadgeCheck }
];

const STATUS_MAPPING = {
  'prete': 'prete_au_chargement',
  'chargement': 'en_cours_de_chargement',
  'en_route': 'charge_en_route'
};

// Statuts finaux (après acceptation)
const FINAL_STATUSES = ['devis_accepte', 'planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree', 'terminee'];
const REJECTED_STATUSES = ['devis_refuse', 'annulee_admin', 'annulee_par_client'];
const EXPIRED_STATUSES = ['devis_expire'];

const CompactTimeline = ({ stages, currentStatus, color, statusMapping = {} }) => {
  const normalizedStatus = statusMapping[currentStatus] || currentStatus;
  const currentIndex = stages.findIndex(s => s.id === normalizedStatus);
  
  // Classes CSS complètes pour chaque couleur
  const colorClasses = {
    purple: {
      active: 'bg-purple-500 text-white',
      current: 'bg-purple-500 text-white ring-2 ring-purple-300 ring-offset-1',
      line: 'bg-purple-500'
    },
    blue: {
      active: 'bg-blue-500 text-white',
      current: 'bg-blue-500 text-white ring-2 ring-blue-300 ring-offset-1',
      line: 'bg-blue-500'
    },
    emerald: {
      active: 'bg-emerald-500 text-white',
      current: 'bg-emerald-500 text-white ring-2 ring-emerald-300 ring-offset-1',
      line: 'bg-emerald-500'
    },
    green: {
      active: 'bg-green-500 text-white',
      current: 'bg-green-500 text-white ring-2 ring-green-300 ring-offset-1',
      line: 'bg-green-500'
    }
  };
  
  const classes = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className="flex items-center gap-1">
      {stages.map((stage, index) => {
        const isPast = currentIndex !== -1 && index < currentIndex;
        const isCurrent = index === currentIndex;
        const Icon = stage.icon;
        
        return (
          <TooltipProvider key={stage.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center">
                  <div className={`
                    w-6 h-6 rounded-full flex items-center justify-center transition-all
                    ${isPast ? classes.active : ''}
                    ${isCurrent ? classes.current : ''}
                    ${!isPast && !isCurrent ? 'bg-gray-100 text-gray-400' : ''}
                  `}>
                    <Icon className="w-3 h-3" />
                  </div>
                  {index < stages.length - 1 && (
                    <div className={`w-3 h-0.5 ${
                      isPast ? classes.line : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {stage.label}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
};

const CompactDualWorkflow = ({ operation, showCommercial = false, onStageClick }) => {
  if (!operation) return null;

  const {
    status,
    financialStatus = 'pending',
    invoiceNumber,
    invoiceAmount,
    amountPaid,
    negotiationRoundsUsed = 0,
    maxNegotiationRounds = 2,
    quoteVersion = 1
  } = operation;

  // Vérifier si on est avant la validation du devis
  const preQuoteStatuses = ['demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client', 'en_negociation'];
  const isPreQuote = preQuoteStatuses.includes(status);
  const isRejected = REJECTED_STATUSES.includes(status);
  const isExpired = EXPIRED_STATUSES.includes(status);
  const isCompleted = FINAL_STATUSES.includes(status);

  // Calculer le pourcentage payé
  const paymentPercent = invoiceAmount && amountPaid 
    ? Math.min(100, Math.round((amountPaid / invoiceAmount) * 100)) 
    : 0;

  // Pour la timeline commerciale, déterminer le statut effectif
  const effectiveCommercialStatus = isCompleted ? 'devis_accepte' : status;

  return (
    <div className="bg-white border rounded-lg p-3 space-y-3">
      {/* Workflow Commercial (Admin only) */}
      {showCommercial && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 min-w-[80px]">
            <FileText className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-gray-600">Commercial</span>
          </div>
          
          {isRejected || isExpired ? (
            <Badge variant="destructive" className="text-xs">
              {isExpired ? 'Expiré' : 'Refusé'}
            </Badge>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <CompactTimeline 
                stages={COMMERCIAL_STAGES}
                currentStatus={effectiveCommercialStatus}
                color="purple"
              />
              {/* Badges info commercial */}
              <div className="flex items-center gap-1 ml-2">
                {quoteVersion > 1 && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    v{quoteVersion}
                  </Badge>
                )}
                {negotiationRoundsUsed > 0 && (
                  <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0">
                    {negotiationRoundsUsed}/{maxNegotiationRounds} négo
                  </Badge>
                )}
                {isCompleted && (
                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                    ✓ Validé
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        {/* Workflow Opération */}
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-1.5 min-w-[80px]">
            <Truck className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-600">Opération</span>
          </div>
          {isPreQuote || isRejected || isExpired ? (
            <Badge variant="secondary" className="text-xs">
              {isRejected || isExpired ? 'Annulée' : 'En attente devis'}
            </Badge>
          ) : (
            <CompactTimeline 
              stages={OPERATION_STAGES}
              currentStatus={status}
              color="blue"
              statusMapping={STATUS_MAPPING}
            />
          )}
        </div>

        {/* Séparateur */}
        <div className="hidden lg:block w-px h-8 bg-gray-200" />

        {/* Workflow Finance */}
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-1.5 min-w-[80px]">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Finance</span>
          </div>
          {isPreQuote || isRejected || isExpired ? (
            <Badge variant="secondary" className="text-xs">
              {isRejected || isExpired ? 'Annulée' : 'En attente'}
            </Badge>
          ) : (
            <>
              <CompactTimeline 
                stages={FINANCE_STAGES}
                currentStatus={financialStatus}
                color="green"
              />
              {/* Badges info */}
              <div className="flex items-center gap-1 ml-2">
                {invoiceNumber && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {invoiceNumber}
                  </Badge>
                )}
                {financialStatus === 'paid' && (
                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                    ✓ Soldée
                  </Badge>
                )}
                {financialStatus === 'partially_paid' && (
                  <Badge className="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0">
                    {paymentPercent}%
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Montants (optionnel, affiché si facturé) */}
      {invoiceAmount && financialStatus !== 'pending' && financialStatus !== 'quote_accepted' && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
          <span>Facture: <span className="font-medium text-gray-700">{invoiceAmount.toLocaleString('fr-FR')} XOF</span></span>
          {amountPaid > 0 && (
            <span>Payé: <span className="font-medium text-green-600">{amountPaid.toLocaleString('fr-FR')} XOF</span></span>
          )}
          {amountPaid > 0 && amountPaid < invoiceAmount && (
            <span>Reste: <span className="font-medium text-orange-600">{(invoiceAmount - amountPaid).toLocaleString('fr-FR')} XOF</span></span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ClientCompactWorkflow - Version client (sans phase commerciale)
 * Affiche uniquement les workflows Opération et Finance
 */
export const ClientCompactWorkflow = ({ operation, onStageClick }) => {
  return (
    <CompactDualWorkflow 
      operation={operation}
      showCommercial={false}
      onStageClick={onStageClick}
    />
  );
};

/**
 * AdminCompactWorkflow - Version admin (avec phase commerciale)
 * Affiche les 3 workflows : Commercial, Opération, Finance
 */
export const AdminCompactWorkflow = ({ operation, onStageClick }) => {
  return (
    <CompactDualWorkflow 
      operation={operation}
      showCommercial={true}
      onStageClick={onStageClick}
    />
  );
};

export default CompactDualWorkflow;
